/**
 * The provider stack, in the one order that works.
 *
 * QueryClientProvider
 *   └ AuthProvider     calls generated hooks, so it must be under the query client
 *       └ RouterProvider
 *
 * `AuthProvider` sits ABOVE the router on purpose: a screen calls `useAuth()`,
 * and the shell renders the signed-in fan's controls. Route GUARDS never read
 * this context — they call the synchronous `getAuthState()` instead, which is
 * what lets them run before any of this has mounted. That is also why
 * `AuthProvider` holds the router back for one API call on a boot where the
 * access token has expired but the refresh token has not: the guard would
 * otherwise read the dead cookie and redirect to sign-in before anyone had
 * asked the server whether the session was still good.
 *
 * There is no toast provider here, and there is not going to be one. This
 * product has no toasts and no snackbars, for success or for error, on any
 * platform including Android. An outcome is announced by something the fan
 * reads at their own pace: `InlineBanner` or `SavedNote` from `@sportsmet/ui`,
 * a screen-level confirmation state, or a sheet.
 */
import { type QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { AuthProvider } from "./auth/AuthProvider";
import type { typeAppRouter } from "./router";

export const App = ({
  queryClient,
  router,
}: {
  queryClient: QueryClient;
  router: typeAppRouter;
}): ReactNode => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  </QueryClientProvider>
);
